/** Linha de comissão lida de planilha/CSV (leitura local no navegador). */
export type FinanceiroComissaoRow = {
  pasta: string
  arquivo: string
  codigoCorretor: string
  nomeCorretor: string
  cpfCnpj: string
  apolice: string
  numeroSub: string
  modulo: string
  estipulante: string
  subEstipulante: string
  fatura: string
  competencia: string
  valorApropriado: number | null
  tipoComissao: string
  parcelaInicial: string
  numeroGrupoCobertura: string
  valorBase: number | null
  percentualComissao: number | null
  valorComissao: number | null
  dataEmissao: string
  dataApropriacao: string
  valorPremioBruto: number | null
  valorPremioTarifario: number | null
  dataMovimento: string
  dataAgendamento: string
  dataPagamento: string
  dataPagamentoEfetivo: string
  statusRecibo: string
  nrCobranca: string
}

export type FinanceiroComissaoFileStatus = 'pendente' | 'lendo' | 'ok' | 'erro'

export type FinanceiroComissaoFileProgress = {
  id: string
  pasta: string
  fileName: string
  relativePath: string
  status: FinanceiroComissaoFileStatus
  rowCount: number
  error?: string
}

export type FinanceiroComissaoSubpasta = {
  name: string
  files: File[]
  fileCount: number
}

/** Subpasta descoberta na pasta raiz (ainda sem carregar arquivos). */
export type FinanceiroComissaoSubpastaInfo = {
  name: string
  year: string | null
}

export type FinanceiroComissaoRootListing = {
  rootName: string
  years: string[]
  subfolders: FinanceiroComissaoSubpastaInfo[]
}

export type FinanceiroComissoesIcatuPastaStatus = 'processing' | 'complete'

/** Registro de pasta já processada no banco. */
export type FinanceiroComissoesIcatuPasta = {
  id: string
  folder_name: string
  year: string | null
  root_folder_name: string | null
  files_ok: number
  files_error: number
  row_count: number
  /** Soma de valor_comissao da pasta. Preenchida após o script financeiro_06. */
  commission_total?: number | null
  status: FinanceiroComissoesIcatuPastaStatus
  processed_by: string | null
  processed_at: string
  created_at: string
  updated_at: string
}

export type FinanceiroComissoesIcatuLinha = {
  id: string
  folder_id: string
  folder_name: string
  file_name: string
  // Spreadsheet fields (Portuguese)
  codigo_corretor: string
  nome_corretor: string
  cpf_cnpj: string
  apolice: string
  numero_sub: string
  modulo: string
  estipulante: string
  sub_estipulante: string
  fatura: string
  competencia: string
  valor_apropriado: number | null
  tipo_comissao: string
  parcela_inicial: string
  numero_grupo_cobertura: string
  valor_base: number | null
  percentual_comissao: number | null
  valor_comissao: number | null
  data_emissao: string
  data_apropriacao: string
  valor_premio_bruto: number | null
  valor_premio_tarifario: number | null
  data_movimento: string
  data_agendamento: string
  data_pagamento: string
  data_pagamento_efetivo: string
  status_recibo: string
  nr_cobranca: string
  uploaded_by: string | null
  created_at: string
  updated_at: string
}

export type FinanceiroComissaoSaveFolderStatus =
  | 'pendente'
  | 'gravando'
  | 'ok'
  | 'erro'
  | 'ja_processada'

/** Andamento da gravação de uma pasta no banco. */
export type FinanceiroComissaoSaveFolderProgress = {
  pasta: string
  status: FinanceiroComissaoSaveFolderStatus
  totalRows: number
  savedRows: number
  error?: string
}

export type SaveComissoesIcatuProgressEvent = {
  pastaNome: string
  savedRows: number
  /** Total conhecido até o momento (pode crescer arquivo a arquivo). */
  totalRows: number
}

export type BeginComissoesIcatuPastaInput = {
  pastaNome: string
  ano: string | null
  rootFolderName: string | null
  userId: string | null
}

export type AppendComissoesIcatuRowsInput = {
  folderId: string
  pastaNome: string
  rows: FinanceiroComissaoRow[]
  userId: string | null
  /** Linhas já gravadas nesta pasta antes deste lote de arquivo. */
  savedRowsBefore: number
  onProgress?: (event: SaveComissoesIcatuProgressEvent) => void
}

export type CompleteComissoesIcatuPastaInput = {
  folderId: string
  arquivosOk: number
  arquivosErro: number
  rowCount: number
  commissionTotal?: number | null
}

export class FinanceiroPastaJaProcessadaError extends Error {
  readonly pastaNome: string

  constructor(pastaNome: string) {
    super(`A pasta "${pastaNome}" já foi processada.`)
    this.name = 'FinanceiroPastaJaProcessadaError'
    this.pastaNome = pastaNome
  }
}

/** Filtros da consulta de linhas já gravadas (finan_icatu_commissions). */
export type FinanceiroComissoesConsultaFilters = {
  /** Vazio ou omitido = todas as pastas. */
  folderNames?: string[] | null
  /** Dia no formato YYYY-MM-DD (input date). */
  dataPagamentoEfetivo?: string | null
  tipoComissao?: string | null
  statusRecibo?: string | null
  search?: string | null
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  page: number
  pageSize: number
}

export type FinanceiroComissoesConsultaResult = {
  rows: FinanceiroComissoesIcatuLinha[]
  total: number
}

/** Totais do filtro atual, não só da página visível. */
export type FinanceiroComissoesResumo = {
  /** Null quando a contagem exata vem da consulta paginada. */
  totalRegistros: number | null
  totalComissao: number | null
}

/** Filtros da aba Análise comparativa (comissões × extratos por período). */
export type FinanceiroAnaliseComparativaFilters = {
  /** CPF/CNPJ informado (com ou sem máscara). */
  cpfCnpj?: string | null
  tipoComissao?: string | null
  statusRecibo?: string | null
  /** Início do período YYYY-MM-DD — pagamento efetivo (comissões). */
  dataPagamentoEfetivoDe?: string | null
  /** Fim do período YYYY-MM-DD — pagamento efetivo (comissões). */
  dataPagamentoEfetivoAte?: string | null
  /** Início do período YYYY-MM-DD — pasta ou data/hora do extrato. */
  extratoDataDe?: string | null
  /** Fim do período YYYY-MM-DD — pasta ou data/hora do extrato. */
  extratoDataAte?: string | null
  account?: string | null
  identification?: string | null
  /** debit | credit | null (todos). */
  type?: 'debit' | 'credit' | null
}

/** Uma linha agregada por dia. */
export type FinanceiroAnaliseComparativaLinha = {
  /** ISO date YYYY-MM-DD. */
  dia: string
  totalComissao: number
  totalExtrato: number
}

export type FinanceiroAnaliseComparativaResult = {
  rows: FinanceiroAnaliseComparativaLinha[]
}

/** Filtros para listar os itens que compõem Comissão Icatu de um dia. */
export type FinanceiroAnaliseComissaoItensFilters = {
  /** ISO date YYYY-MM-DD. */
  dia: string
  cpfCnpj?: string | null
  tipoComissao?: string | null
  statusRecibo?: string | null
  page?: number
  pageSize?: number
}

/** Linha deduplicada que entra na soma Comissão Icatu (R$) do dia. */
export type FinanceiroAnaliseComissaoItem = {
  id: string
  nomeCorretor: string
  cpfCnpj: string
  apolice: string
  fatura: string
  nrCobranca: string
  tipoComissao: string
  valorComissao: number
  dataPagamentoEfetivo: string
  statusRecibo: string
  folderName: string
}

export type FinanceiroAnaliseComissaoItensResult = {
  rows: FinanceiroAnaliseComissaoItem[]
  total: number
}
